/**
 * Client for the Cashback module (`/cashback`). A money-valued customer wallet
 * backed by the GL — config, per-customer balances, the points ledger, and
 * manual adjustments.
 */
import type {
  CashbackBalance, CashbackConfig, CashbackConfigUpdate,
  CashbackLedgerEntry, CashbackSummary, CashbackTxnType,
} from '@erp/shared';

import { api } from './api.js';

export type { CashbackConfig, CashbackBalance, CashbackLedgerEntry, CashbackSummary } from '@erp/shared';

export async function getCashbackConfig(): Promise<CashbackConfig> {
  return (await api().get<CashbackConfig>('/cashback/config')).data;
}

export async function setCashbackConfig(patch: CashbackConfigUpdate): Promise<CashbackConfig> {
  return (await api().put<CashbackConfig>('/cashback/config', patch)).data;
}

export async function ensureCashbackAccounts(): Promise<CashbackConfig> {
  return (await api().post<CashbackConfig>('/cashback/accounts/ensure', {})).data;
}

export async function getCashbackSummary(): Promise<CashbackSummary> {
  return (await api().get<CashbackSummary>('/cashback/summary')).data;
}

export async function listCashbackBalances(): Promise<CashbackBalance[]> {
  return (await api().get<{ items: CashbackBalance[] }>('/cashback/balances')).data.items;
}

/** A single customer's wallet balance (base currency). 0 if none / on error. */
export async function getCashbackBalance(partyId: string): Promise<string> {
  try {
    const r = await api().get<{ partyId: string; balance: string }>(`/cashback/balances/${partyId}`);
    return r.data.balance;
  } catch {
    return '0';
  }
}

export async function listCashbackLedger(
  params?: { partyId?: string; type?: CashbackTxnType; from?: string; to?: string; limit?: number },
): Promise<CashbackLedgerEntry[]> {
  return (await api().get<{ items: CashbackLedgerEntry[] }>('/cashback/ledger', { params })).data.items;
}

export async function adjustCashback(
  input: { partyId: string; amount: string; note?: string },
): Promise<{ balanceAfter: string }> {
  return (await api().post<{ balanceAfter: string }>('/cashback/adjust', input)).data;
}

/** Expire cashback older than the configured window (FIFO). Admin/cron trigger. */
export async function expireCashback(): Promise<{ partiesExpired: number; totalExpired: string }> {
  return (await api().post<{ partiesExpired: number; totalExpired: string }>('/cashback/expire', {})).data;
}

/** Redeem cashback against a posted invoice (creates a cashback payment). */
export async function redeemCashback(
  input: { invoiceId: string; amount: string },
): Promise<{ balanceAfter: string; paymentId: string }> {
  return (await api().post<{ balanceAfter: string; paymentId: string }>('/cashback/redeem', input)).data;
}
